import type { GetAccountContextResp } from "@/types/accounts.ts";
import _markerSDKLoader, { type MarkerSdk, type MarkerSdkLoader } from "@marker.io/browser";
import { api } from "@/lib/http.ts";
import { getDefaultStore } from "jotai";
import { activeThreadIdsAtom } from "../threads/hooks/useActiveThreadTracking.ts";
import { getThreadIdFromPath } from "../threads/util.ts";

const markerSdkLoader = _markerSDKLoader as unknown as MarkerSdkLoader; // their types are messed up

const MARKER_PROJECT_ID = import.meta.env.VITE_MARKER_PROJECT_ID;

if (!MARKER_PROJECT_ID) {
  throw new Error("VITE_MARKER_PROJECT_ID environment variable is not set");
}

// singleton widget promise
let initializeMarker: Promise<MarkerSdk> | null = null;
let currentUserId: number | null = null;

type CaptureState = {
  capturedAt: number;
  setCompletedAt: number | null;
  raceLogged: boolean;
  threadIds: number[];
};

/**
 * Register the SDK event listeners that emit our customData and correlation
 * logs. Called exactly once per widget singleton.
 *
 * Per-capture-cycle state is keyed by a short captureId minted in the capture
 * handler and echoed through every log line for that cycle, so a Linear issue
 * (which lands shortly after feedbacksent) can be traced back to the exact
 * capture that produced it via Statsig session replay.
 */
const setupMarkerEventListeners = (marker: MarkerSdk) => {
  const captures = new Map<string, CaptureState>();
  let latestCaptureId: string | null = null;

  // @see https://help.marker.io/en/articles/4621840-widget-javascript-sdk
  // biome-ignore lint/nursery/noMisusedPromises: this is just async for internal control flow
  marker.on("capture", async () => {
    const captureId = crypto.randomUUID().slice(0, 8);
    const state: CaptureState = {
      capturedAt: Date.now(),
      setCompletedAt: null,
      raceLogged: false,
      threadIds: [],
    };
    captures.set(captureId, state);
    latestCaptureId = captureId;
    // Bound the Map: a single capture cycle resolves in seconds, so 60s is
    // generous and prevents unbounded growth across a long-lived session.
    setTimeout(() => captures.delete(captureId), 60_000);

    const store = getDefaultStore();
    // The thread in the URL is the one the report is about, so it must always be
    // attached — even if it hasn't registered in the active-thread atom yet (a
    // slow-loading or erroring page may never register). The atom's other entries
    // (e.g. a thread peeked in the right sidebar) are kept as secondary context.
    const viewedThreadId = getThreadIdFromPath(window.location.pathname);
    const activeThreadIds = Array.from(store.get(activeThreadIdsAtom));
    const threadIds =
      viewedThreadId !== undefined && !activeThreadIds.includes(viewedThreadId)
        ? [viewedThreadId, ...activeThreadIds]
        : activeThreadIds;
    state.threadIds = threadIds;

    // Upload debug data for each thread. A failed upload becomes an explicit
    // marker rather than a silent omission: dropping it would leave a report
    // about the viewed thread carrying only some *other* active thread's link,
    // which reads as a debug link pointing at the wrong thread (BIG-6229).
    const debugLinks = await Promise.all(
      threadIds.map(async (threadId) => {
        try {
          const response = await api.post<{ id: string; url: string }>(
            `/api/a/threads/${threadId}/messages/debug/list`,
          );
          return { threadId, value: response.data.url };
        } catch (error) {
          console.warn(`Failed to upload debug data for thread ${threadId}:`, error);
          return { threadId, value: "⚠️ debug upload failed" };
        }
      }),
    );

    const debugLinksMap = Object.fromEntries(debugLinks.map((link) => [link.threadId, link.value]));

    marker.setCustomData({
      userId: String(currentUserId),
      // Name the thread the report is about so a secondary/sidebar link can't be
      // mistaken for it.
      viewedThreadId:
        viewedThreadId !== undefined ? String(viewedThreadId) : "none (not on a thread page)",
      ...debugLinksMap,
      FYI: "Debug links work locally too, just replace admin.okthx.com with localhost:5174",
    });
    state.setCompletedAt = Date.now();
    // If the race already fired, this info log is meaningless for the current
    // report (the data went out without customData) — emit a distinct message
    // so Statsig session search isn't misled by a trailing happy-path log.
    const message = state.raceLogged
      ? "[marker.io] customData set after race — too late for this report"
      : "[marker.io] customData set";
    console.info(message, {
      captureId,
      capture_to_set_ms: state.setCompletedAt - state.capturedAt,
      threadIds: state.threadIds,
      userId: currentUserId,
    });
  });

  marker.on("feedbackbeforesend", () => {
    if (!latestCaptureId) return;
    const state = captures.get(latestCaptureId);
    if (!state) return;
    if (state.setCompletedAt === null) {
      state.raceLogged = true;
      console.error(
        "[marker.io] customData race detected — submit fired before setCustomData completed",
        {
          captureId: latestCaptureId,
          capture_to_submit_ms: Date.now() - state.capturedAt,
          threadIds: state.threadIds,
          userId: currentUserId,
        },
      );
    }
  });

  // Correlation logs — let Statsig session replays match a Linear issue to
  // the capture cycle that produced it. Without these, we know setCustomData
  // ran but not which cycle's customData actually shipped (capture can fire
  // multiple times in a session, e.g. retried after a screen-capture failure).
  // `event` is the marker.io outcome name minus its `feedback` prefix.
  // State may be absent if the captureId's 60s TTL already elapsed — in that
  // case threadIds/ms_since_capture are nullish but captureId still correlates.
  const logFeedbackOutcome = (event: "sent" | "discarded" | "error") => {
    const state = latestCaptureId ? captures.get(latestCaptureId) : null;
    const logFn = event === "error" ? console.error : console.info;
    logFn(`[marker.io] feedback ${event}`, {
      captureId: latestCaptureId,
      threadIds: state?.threadIds ?? [],
      userId: currentUserId,
      ms_since_capture: state ? Date.now() - state.capturedAt : null,
    });
  };
  marker.on("feedbacksent", () => logFeedbackOutcome("sent"));
  marker.on("feedbackdiscarded", () => logFeedbackOutcome("discarded"));
  marker.on("feedbackerror", () => logFeedbackOutcome("error"));
};

/** Initialize the Marker.io widget singleton. */
export const initializeMarkerSingleton = async (userContext: GetAccountContextResp) => {
  currentUserId = userContext.user.id;

  if (!initializeMarker) {
    // don't await here - we synchronously define initializeWidget so we can avoid re-initializing with the "if" above
    // if we awaited, we could kick off init N times while the promise is loading, which is noisy.
    initializeMarker = markerSdkLoader.loadWidget({
      project: MARKER_PROJECT_ID,
    });

    const marker = await initializeMarker;
    setupMarkerEventListeners(marker);
  }
  return initializeMarker;
};
